import React, { useState } from 'react';
import Icon from '../components/Icon';
import FAQ from '../components/FAQ';
import TestimonialCard from '../components/TestimonialCard';
import PainPointCard from '../components/PainPointCard';
import JourneyTimeline from '../components/JourneyTimeline';
import useReveal from '../hooks/useReveal';
import {
  PAIN_POINTS, TESTIMONIALS, SOCIAL_METRICS, FAQS, JOURNEY_STEPS,
} from '../data';

const HOW_STEPS = [
  { n:'01', icon:'search',       title:'Busque seu serviço',   desc:'Informe seu CEP e o serviço que precisa. Comparamos preços de todas as oficinas da sua região em segundos.' },
  { n:'02', icon:'trending-up',  title:'Seu score, seu desconto', desc:'Conecte Uber ou 99. Quanto mais corridas e melhor sua nota, maior o desconto — até 25% OFF garantido.' },
  { n:'03', icon:'calendar',     title:'Agende com 1 clique',  desc:'Escolha a oficina, a data e o horário. Confirmação imediata. Tudo pelo app, sem ligação, sem fila.' },
];

const STATS = [
  { value:'12.000+', label:'motoristas ativos' },
  { value:'300+',    label:'oficinas credenciadas' },
  { value:'4.8',     label:'avaliação média' },
  { value:'25%',     label:'desconto máximo' },
];

const TRUST_LOGOS = ['Uber','99','iFood','Rappi','InDriver'];

const SCORE_LEVELS = [
  { lvl:'Iniciante', pts:'0–34',  disc:'5%',  color:'#6B7280' },
  { lvl:'Bronze',    pts:'35–54', disc:'10%', color:'#CD7F32' },
  { lvl:'Prata',     pts:'55–74', disc:'15%', color:'#C0C0C0' },
  { lvl:'Ouro',      pts:'75–89', disc:'20%', color:'#FFA500' },
  { lvl:'Elite',     pts:'90+',   disc:'25%', color:'#FFD700' },
];

// Reveal-on-scroll wrapper
function Reveal({ children, as: Tag = 'div', delay = 0, ...rest }) {
  const [ref, visible] = useReveal();
  const style = {
    ...(rest.style || {}),
    transitionDelay: `${delay}ms`,
  };
  return (
    <Tag
      ref={ref}
      {...rest}
      style={style}
      className={`reveal ${visible ? 'is-visible' : ''} ${rest.className || ''}`.trim()}
    >
      {children}
    </Tag>
  );
}

export default function LandingScreen({ onLogin, onRegister }) {
  const [cep, setCep] = useState('');

  return (
    <div style={{ minHeight: '100vh', background: '#0D0D0D' }}>

      {/* ── NAV ── */}
      <nav className="nav">
        <div className="nav-logo">
          <img src="/assets/logo-dark.jpeg" alt="AutoFácil" className="nav-logo-img" />
          <span style={{ color:'#fff' }}>Auto</span><span>Fácil</span>
        </div>
        <ul className="nav-links">
          <li><a href="#dores">Por que AutoFácil</a></li>
          <li><a href="#solucao">Solução</a></li>
          <li><a href="#depoimentos">Depoimentos</a></li>
          <li><a href="#faq">FAQ</a></li>
        </ul>
        <div style={{ display:'flex', gap:10, marginLeft:'auto' }} className="nav-cta">
          <button className="btn btn-outline" style={{ padding:'10px 20px', fontSize:14 }} onClick={onLogin}>Entrar</button>
          <button className="btn btn-primary" style={{ padding:'10px 20px', fontSize:14 }} onClick={onRegister}>Quero meus descontos</button>
        </div>
      </nav>

      {/* ════════════════════════════════════════════════════════════════ */}
      {/* SEÇÃO 1 — HERO (Topo de funil / Nível 1-2)                       */}
      {/* ════════════════════════════════════════════════════════════════ */}
      <section style={{
        minHeight:'100vh',
        display:'flex',
        flexDirection:'column',
        justifyContent:'center',
        padding:'clamp(100px, 12vh, 160px) clamp(20px, 5vw, 80px) 80px',
        position:'relative',
        overflow:'hidden',
        maxWidth:1200,
        margin:'0 auto',
      }}>
        <div style={{
          position:'absolute', top:'15%', right:'-5%',
          width:500, height:500, borderRadius:'50%',
          background:'radial-gradient(circle, rgba(255,92,0,0.12) 0%, transparent 65%)',
          pointerEvents:'none',
        }} />
        <div style={{
          position:'absolute', bottom:'0', left:'-10%',
          width:400, height:400, borderRadius:'50%',
          background:'radial-gradient(circle, rgba(255,92,0,0.06) 0%, transparent 70%)',
          pointerEvents:'none',
        }} />

        <div className="fade-up" style={{ maxWidth:780, position:'relative' }}>
          <div className="badge badge-orange" style={{ marginBottom:20 }}>
            Exclusivo para motoristas de aplicativo
          </div>

          <h1 className="display-xl" style={{ marginBottom:20, color:'#fff' }}>
            Você roda o dia inteiro e quando o carro quebra,{' '}
            <span style={{ color:'var(--orange)', display:'inline-block' }}>o lucro vai embora?</span>
          </h1>

          <p className="body-lg" style={{ maxWidth:620, marginBottom:32 }}>
            A AutoFácil conecta motoristas de app a oficinas credenciadas com até{' '}
            <strong style={{ color:'#fff' }}>25% de desconto</strong> — quanto mais você roda, mais você economiza. Cadastro 100% gratuito.
          </p>

          {/* CTAs */}
          <div className="hero-ctas" style={{ display:'flex', gap:12, marginBottom:28, flexWrap:'wrap' }}>
            <button
              className="btn btn-primary"
              style={{ padding:'16px 32px', fontSize:16 }}
              onClick={onRegister}
            >
              Quero meus descontos <Icon name="arrow-right" size={18} />
            </button>
            <a
              href="#dores"
              className="btn btn-outline"
              style={{ padding:'16px 28px', fontSize:15 }}
            >
              Veja como funciona
            </a>
          </div>

          {/* Search Widget */}
          <div style={{
            background:'var(--surface)',
            border:'1px solid var(--border-lt)',
            borderRadius:'var(--radius-xl)',
            padding:8,
            display:'flex',
            gap:8,
            maxWidth:560,
            marginBottom:28,
            boxShadow:'0 4px 40px rgba(0,0,0,0.4)',
          }}>
            <input
              className="input"
              placeholder="Digite seu CEP e veja oficinas perto de você"
              value={cep}
              onChange={e => setCep(e.target.value)}
              style={{ border:'none', background:'transparent', flex:1 }}
            />
            <button className="btn btn-primary" style={{ borderRadius:12, padding:'12px 22px' }} onClick={onRegister}>
              Buscar
            </button>
          </div>
        </div>

        {/* Stats strip — prova social rápida */}
        <div className="stats-grid fade-up" style={{
          display:'grid',
          gridTemplateColumns:'repeat(4,1fr)',
          gap:16,
          marginTop:48,
          padding:'24px 28px',
          background:'var(--surface)',
          border:'1px solid var(--border)',
          borderRadius:'var(--radius-xl)',
          maxWidth:780,
          animationDelay:'0.15s',
        }}>
          {STATS.map(s => (
            <div key={s.label} style={{ textAlign:'center' }}>
              <div style={{ fontFamily:'var(--font-display)', fontSize:26, fontWeight:800, color:'var(--orange)', marginBottom:2 }}>{s.value}</div>
              <div style={{ fontSize:12, color:'var(--grey-3)' }}>{s.label}</div>
            </div>
          ))}
        </div>

        {/* Trust logos */}
        <div style={{ marginTop:40, position:'relative' }}>
          <p className="label" style={{ color:'var(--grey-4)', marginBottom:16 }}>
            compatível com os apps que você já usa
          </p>
          <div style={{ display:'flex', gap:14, flexWrap:'wrap' }}>
            {TRUST_LOGOS.map(logo => (
              <div key={logo} style={{
                padding:'10px 18px',
                background:'var(--surface)',
                border:'1px solid var(--border)',
                borderRadius:'var(--radius-md)',
                fontFamily:'var(--font-display)',
                fontWeight:700,
                fontSize:13,
                color:'var(--grey-3)',
              }}>{logo}</div>
            ))}
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════════════════════ */}
      {/* SEÇÃO 2 — DOR DO CLIENTE (Topo de funil / Nível 2)               */}
      {/* ════════════════════════════════════════════════════════════════ */}
      <section id="dores" style={{
        padding:'clamp(60px,8vw,100px) clamp(20px,5vw,80px)',
        background:'var(--surface)',
        borderTop:'1px solid var(--border)',
        borderBottom:'1px solid var(--border)',
      }}>
        <div style={{ maxWidth:1200, margin:'0 auto' }}>
          <Reveal className="section-head">
            <div className="badge badge-orange">
              A dor do motorista
            </div>
            <h2 className="display-lg">Você se identifica<br/>com alguma dessas situações?</h2>
            <p className="body-lg">
              Levamos tempo conversando com centenas de motoristas e ouvimos sempre as mesmas histórias.
              Veja se uma delas é a sua.
            </p>
          </Reveal>

          <div className="pain-grid">
            {PAIN_POINTS.map((pain, i) => (
              <Reveal key={pain.title} delay={i * 80}>
                <PainPointCard pain={pain} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════════════════════ */}
      {/* SEÇÃO 3 — PROPOSTA DE VALOR / SOLUÇÃO (Meio de funil / Nível 3)  */}
      {/* ════════════════════════════════════════════════════════════════ */}
      <section id="solucao" style={{ padding:'clamp(60px,8vw,100px) clamp(20px,5vw,80px)' }}>
        <div style={{ maxWidth:1200, margin:'0 auto' }}>
          <Reveal className="section-head">
            <div className="badge badge-orange">
              A solução
            </div>
            <h2 className="display-lg">
              A primeira plataforma que <span style={{ color:'var(--orange)' }}>te recompensa</span> por rodar bem.
            </h2>
            <p className="body-lg">
              3 pilares que resolvem de uma vez todas as dores que você acabou de ler.
            </p>
          </Reveal>

          {/* Pillars */}
          <div className="pillars-grid" style={{ marginBottom:64 }}>
            {/* Pillar 1 — Score → Desconto */}
            <Reveal delay={0}>
              <article className="card pillar-card">
                <div className="pillar-icon"><Icon name="percent" size={26} /></div>
                <h3 className="pillar-title">Economia real e progressiva</h3>
                <p className="pillar-desc">
                  Seu score de motorista vale desconto. Conecte Uber ou 99 e ganhe de 5% a 25% OFF em qualquer serviço — visível em 10 segundos.
                </p>
                <ul className="pillar-bullets">
                  <li><Icon name="check" size={16} /> Todo motorista começa com 5% OFF</li>
                  <li><Icon name="check" size={16} /> Sem mínimo de corridas</li>
                  <li><Icon name="check" size={16} /> Atualização automática do score</li>
                </ul>
              </article>
            </Reveal>

            {/* Pillar 2 — Oficinas verificadas */}
            <Reveal delay={120}>
              <article className="card pillar-card">
                <div className="pillar-icon"><Icon name="shield-check" size={26} /></div>
                <h3 className="pillar-title">Oficinas verificadas e avaliadas</h3>
                <p className="pillar-desc">
                  Todas as oficinas são credenciadas e avaliadas por motoristas como você. Sem surpresa, sem serviço inventado.
                </p>
                <ul className="pillar-bullets">
                  <li><Icon name="check" size={16} /> Credenciamento com critérios</li>
                  <li><Icon name="check" size={16} /> Nota mínima 4.0 obrigatória</li>
                  <li><Icon name="check" size={16} /> Você avalia depois do serviço</li>
                </ul>
              </article>
            </Reveal>

            {/* Pillar 3 — Agendamento rápido */}
            <Reveal delay={240}>
              <article className="card pillar-card">
                <div className="pillar-icon"><Icon name="zap" size={26} /></div>
                <h3 className="pillar-title">Agendamento rápido, sem burocracia</h3>
                <p className="pillar-desc">
                  Escolha o serviço, a oficina e o horário. Confirmação instantânea. Sem ligação, sem fila, sem perder corrida.
                </p>
                <ul className="pillar-bullets">
                  <li><Icon name="check" size={16} /> Confirmação em 1 clique</li>
                  <li><Icon name="check" size={16} /> Cancelamento gratuito</li>
                  <li><Icon name="check" size={16} /> Lembrete automático</li>
                </ul>
              </article>
            </Reveal>
          </div>

          {/* Score visual + Levels */}
          <Reveal>
            <div style={{
              display:'grid',
              gridTemplateColumns:'1fr 1fr',
              gap:48,
              alignItems:'center',
              padding:'clamp(28px,4vw,48px)',
              background:'var(--surface)',
              border:'1px solid var(--border)',
              borderRadius:'var(--radius-xl)',
            }} className="how-grid">
              <div>
                <div className="badge badge-orange" style={{ marginBottom:16 }}>
                  Quanto mais você roda…
                </div>
                <h3 className="display-md" style={{ color:'#fff', marginBottom:14 }}>
                  …mais você <span style={{ color:'var(--orange)' }}>economiza</span>.
                </h3>
                <p className="body-md" style={{ marginBottom:24 }}>
                  Seu desconto é calculado automaticamente com base no seu histórico nos apps de mobilidade.
                  Conecte e veja seu nível em 10 segundos.
                </p>
                {[
                  ['car',          'Corridas realizadas', '40%'],
                  ['star',         'Avaliação no app',    '35%'],
                  ['calendar',     'Tempo de atuação',    '25%'],
                ].map(([icon, lbl, pct]) => (
                  <div key={lbl} style={{ display:'flex', alignItems:'center', gap:14, marginBottom:14 }}>
                    <div style={{
                      width:36, height:36,
                      background:'var(--orange-dim)',
                      borderRadius:'var(--radius-sm)',
                      display:'flex', alignItems:'center', justifyContent:'center',
                      color:'var(--orange)',
                      flexShrink:0,
                    }}><Icon name={icon} size={18} /></div>
                    <div style={{ flex:1 }}>
                      <div style={{ display:'flex', justifyContent:'space-between', marginBottom:4 }}>
                        <span style={{ fontSize:14, fontWeight:600, color:'#fff' }}>{lbl}</span>
                        <span style={{ fontSize:14, color:'var(--orange)', fontWeight:700 }}>{pct}</span>
                      </div>
                      <div className="progress-track">
                        <div className="progress-fill" style={{ width:pct }} />
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Level cards */}
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                {SCORE_LEVELS.map(item => (
                  <div key={item.lvl} className="card" style={{
                    padding:'18px 16px',
                    borderColor: item.lvl==='Elite' ? 'rgba(255,215,0,0.3)' : undefined,
                  }}>
                    <div style={{ display:'flex', justifyContent:'space-between', marginBottom:8 }}>
                      <span style={{ fontSize:12, fontWeight:700, color:item.color }}>{item.lvl}</span>
                      <span className="badge badge-orange" style={{ padding:'2px 8px', fontSize:11 }}>{item.disc}</span>
                    </div>
                    <div style={{ fontSize:13, color:'var(--grey-3)' }}>{item.pts} pts</div>
                  </div>
                ))}
                <div style={{
                  background:'linear-gradient(135deg, rgba(255,92,0,0.18), rgba(255,215,0,0.10))',
                  border:'1px solid rgba(255,92,0,0.32)',
                  borderRadius:'var(--radius-lg)',
                  padding:'18px 16px',
                  display:'flex', flexDirection:'column', justifyContent:'center',
                  cursor:'pointer',
                }} onClick={onRegister}>
                  <div style={{ fontSize:12, fontWeight:700, color:'var(--orange)', marginBottom:4 }}>Conecte agora</div>
                  <div style={{ fontSize:13, color:'var(--grey-3)' }}>Calcule seu score em 30s</div>
                </div>
              </div>
            </div>
          </Reveal>

          {/* How it works — 3 passos */}
          <Reveal style={{ marginTop:64 }}>
            <h3 className="display-md" style={{ color:'#fff', textAlign:'center', marginBottom:32 }}>
              Como funciona em <span style={{ color:'var(--orange)' }}>3 passos</span>
            </h3>
            <div className="how-grid" style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:20 }}>
              {HOW_STEPS.map((step, i) => (
                <div key={step.n} style={{
                  background:'var(--surface)',
                  border:'1px solid var(--border)',
                  borderRadius:'var(--radius-lg)',
                  padding:'28px 24px',
                  position:'relative',
                  transition:'var(--transition)',
                }}>
                  <div style={{
                    position:'absolute', top:18, right:20,
                    fontFamily:'var(--font-display)',
                    fontSize:48,
                    fontWeight:800,
                    color:'rgba(255,92,0,0.06)',
                    lineHeight:1,
                    userSelect:'none',
                  }}>{step.n}</div>
                  <div style={{
                    width:48, height:48,
                    background:'var(--orange-dim)',
                    borderRadius:'var(--radius-md)',
                    display:'flex', alignItems:'center', justifyContent:'center',
                    color:'var(--orange)',
                    marginBottom:16,
                  }}><Icon name={step.icon} size={22} /></div>
                  <div className="step-number" style={{ marginBottom:14 }}>{i+1}</div>
                  <h4 style={{ fontFamily:'var(--font-display)', fontSize:18, fontWeight:700, color:'#fff', marginBottom:8 }}>{step.title}</h4>
                  <p className="body-md">{step.desc}</p>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ════════════════════════════════════════════════════════════════ */}
      {/* SEÇÃO 4 — PROVA SOCIAL E DEPOIMENTOS (Meio / Nível 3-4)          */}
      {/* ════════════════════════════════════════════════════════════════ */}
      <section id="depoimentos" style={{
        padding:'clamp(60px,8vw,100px) clamp(20px,5vw,80px)',
        background:'var(--surface)',
        borderTop:'1px solid var(--border)',
        borderBottom:'1px solid var(--border)',
      }}>
        <div style={{ maxWidth:1200, margin:'0 auto' }}>
          <Reveal className="section-head">
            <div className="badge badge-orange">
              Quem já usa, recomenda
            </div>
            <h2 className="display-lg">Motoristas como você,<br/>economizando todos os dias.</h2>
            <p className="body-lg">
              Mais de 12.000 motoristas já fazem manutenção pela AutoFácil em São Paulo.
            </p>
          </Reveal>

          <div className="testimonial-grid">
            {TESTIMONIALS.map((t, i) => (
              <Reveal key={t.name} delay={i * 100}>
                <TestimonialCard testimonial={t} />
              </Reveal>
            ))}
          </div>

          <Reveal>
            <div className="social-counters">
              {SOCIAL_METRICS.map(s => (
                <div key={s.label} className="social-counter">
                  <div className="social-counter-value">{s.value}</div>
                  <div className="social-counter-label">{s.label}</div>
                </div>
              ))}
            </div>
          </Reveal>

          {/* Journey */}
          <Reveal style={{ marginTop:80 }}>
            <div className="section-head" style={{ marginBottom:40 }}>
              <div className="badge badge-orange">
                Sua jornada
              </div>
              <h2 className="display-md">Do primeiro clique ao primeiro desconto.</h2>
              <p className="body-md">
                Veja como é a experiência completa do motorista na AutoFácil — passo a passo.
              </p>
            </div>
            <JourneyTimeline steps={JOURNEY_STEPS} />
          </Reveal>
        </div>
      </section>

      {/* ════════════════════════════════════════════════════════════════ */}
      {/* SEÇÃO 5 — REBATE DE OBJEÇÕES / FAQ (Fundo / Nível 4)             */}
      {/* ════════════════════════════════════════════════════════════════ */}
      <section id="faq" style={{ padding:'clamp(60px,8vw,100px) clamp(20px,5vw,80px)' }}>
        <div style={{ maxWidth:1200, margin:'0 auto' }}>
          <Reveal className="section-head">
            <div className="badge badge-orange">
              Sem letra miúda
            </div>
            <h2 className="display-lg">As perguntas que <span style={{ color:'var(--orange)' }}>todo motorista</span> faz.</h2>
            <p className="body-lg">
              Antes de você perguntar, já respondemos. Se ainda tiver dúvida, é só falar com a gente.
            </p>
          </Reveal>

          <Reveal>
            <FAQ items={FAQS} />
          </Reveal>
        </div>
      </section>

      {/* ════════════════════════════════════════════════════════════════ */}
      {/* SEÇÃO 6 — CTA FINAL / CONVERSÃO (Fundo / Nível 5)                */}
      {/* ════════════════════════════════════════════════════════════════ */}
      <section className="final-cta">
        <div className="final-cta-inner">
          <div className="badge badge-white" style={{ marginBottom:18, background:'rgba(255,255,255,0.18)', color:'#fff', borderColor:'rgba(255,255,255,0.25)' }}>
            <Icon name="sparkles" size={14} /> Seus colegas já estão economizando
          </div>
          <h2 className="display-lg">E você, vai continuar pagando caro?</h2>
          <p className="final-cta-sub">
            Cadastro gratuito → conecte seu app → veja seu desconto em 10 segundos. É simples assim.
          </p>

          <button className="final-cta-button btn" onClick={onRegister}>
            Criar minha conta grátis <Icon name="arrow-right" size={18} />
          </button>

          <div className="final-cta-reassure">
            <span><Icon name="check" size={14} /> Sem cartão de crédito</span>
            <span><Icon name="check" size={14} /> Sem compromisso</span>
            <span><Icon name="check" size={14} /> Cancele quando quiser</span>
          </div>

          <div className="final-cta-scarcity">
            <Icon name="zap" size={14} />
            Primeiros 1.000 motoristas ganham nível Bronze direto
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════════════════════ */}
      {/* SEÇÃO 7 — FOOTER                                                 */}
      {/* ════════════════════════════════════════════════════════════════ */}
      <footer className="footer-rich">
        <div className="footer-rich-grid">
          <div>
            <div className="nav-logo" style={{ fontSize:22 }}>
              <img src="/assets/logo-dark.jpeg" alt="AutoFácil" className="nav-logo-img" />
              <span style={{ color:'#fff' }}>Auto</span><span>Fácil</span>
            </div>
            <p className="footer-tagline">
              Feito para quem vive na estrada. Manutenção justa, oficinas verificadas e desconto progressivo para motoristas de aplicativo.
            </p>
          </div>
          <div>
            <div className="footer-col-title">Produto</div>
            <ul className="footer-col-list">
              <li><a href="#solucao">Como funciona</a></li>
              <li><a href="#solucao">Score e descontos</a></li>
              <li><a href="#depoimentos">Depoimentos</a></li>
              <li><a href="#faq">FAQ</a></li>
            </ul>
          </div>
          <div>
            <div className="footer-col-title">Empresa</div>
            <ul className="footer-col-list">
              <li><a href="#sobre">Sobre a AutoFácil</a></li>
              <li><a href="#oficinas">Para oficinas</a></li>
              <li><a href="#imprensa">Imprensa</a></li>
              <li><a href="#contato">Contato</a></li>
            </ul>
          </div>
          <div>
            <div className="footer-col-title">Legal</div>
            <ul className="footer-col-list">
              <li><a href="#termos">Termos de uso</a></li>
              <li><a href="#privacidade">Política de privacidade</a></li>
              <li><a href="#cookies">Política de cookies</a></li>
              <li><a href="#lgpd">LGPD</a></li>
            </ul>
          </div>
        </div>
        <div className="footer-bottom">
          <span>© 2025 AutoFácil — Feito para quem vive na estrada.</span>
          <span>São Paulo, Brasil · contato@autofacil.com.br</span>
        </div>
      </footer>
    </div>
  );
}
